import { useMemo } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { formatMoney, normalizeSearch, variantLabel } from '@pos/core';
import type { CategoryRow, ProductRow, SupplierRow, VariantRow } from '@pos/local-db';
import { useSession } from '../../app/current.tsx';
import { db } from '../../lib/db.ts';

export interface CatalogItem {
  product: ProductRow;
  variants: VariantRow[];
  category: CategoryRow | null;
  supplier: SupplierRow | null;
  /** Texto normalizado para buscar (nombre, códigos, categoría, variantes). */
  search: string;
}

export interface Catalog {
  items: CatalogItem[];
  byId: Map<string, CatalogItem>;
  categories: CategoryRow[];
  suppliers: SupplierRow[];
  /** Todas las variantes vivas (para validar códigos repetidos). */
  variants: VariantRow[];
  /** Costos por variante. Vacío en dispositivos de empleados. */
  costs: Map<string, number>;
}

const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name, 'es');

/** Catálogo completo del negocio, en vivo desde la base local. undefined mientras carga. */
export function useCatalog(): Catalog | undefined {
  const { business } = useSession();
  const raw = useLiveQuery(
    async () => {
      const id = business.id;
      const [products, variants, costs, categories, suppliers] = await Promise.all([
        db.products.where('business_id').equals(id).toArray(),
        db.product_variants.where('business_id').equals(id).toArray(),
        db.variant_costs.where('business_id').equals(id).toArray(),
        db.categories.where('business_id').equals(id).toArray(),
        db.suppliers.where('business_id').equals(id).toArray(),
      ]);
      return { products, variants, costs, categories, suppliers };
    },
    [business.id],
  );

  return useMemo(() => {
    if (!raw) return undefined;
    const categories = raw.categories.filter((c) => !c.deleted_at).sort(byName);
    const suppliers = raw.suppliers.filter((s) => !s.deleted_at).sort(byName);
    const categoryById = new Map(categories.map((c) => [c.id, c]));
    const supplierById = new Map(suppliers.map((s) => [s.id, s]));
    const liveProducts = new Set(raw.products.filter((p) => !p.deleted_at).map((p) => p.id));
    // Variantes vivas de productos vivos (las de un producto eliminado no cuentan, ni para códigos repetidos).
    const variants = raw.variants.filter((v) => !v.deleted_at && liveProducts.has(v.product_id));
    const variantsByProduct = new Map<string, VariantRow[]>();
    for (const v of variants.sort((a, b) => a.sort - b.sort)) {
      const list = variantsByProduct.get(v.product_id) ?? [];
      list.push(v);
      variantsByProduct.set(v.product_id, list);
    }
    const items = raw.products
      .filter((p) => !p.deleted_at)
      .sort(byName)
      .map((product): CatalogItem => {
        const vs = variantsByProduct.get(product.id) ?? [];
        const category = (product.category_id && categoryById.get(product.category_id)) || null;
        const supplier = (product.supplier_id && supplierById.get(product.supplier_id)) || null;
        const search = normalizeSearch(
          [product.name, category?.name, supplier?.name, ...vs.flatMap((v) => [v.barcode, v.sku, variantLabel(v.attributes)])]
            .filter(Boolean)
            .join(' '),
        );
        return { product, variants: vs, category, supplier, search };
      });
    return {
      items,
      byId: new Map(items.map((i) => [i.product.id, i])),
      categories,
      suppliers,
      variants,
      costs: new Map(raw.costs.map((c) => [c.id, c.cost])),
    };
  }, [raw]);
}

/** Formateo de dinero según la configuración del negocio (con o sin centavos). */
export function useMoney(): (cents: number) => string {
  const { business } = useSession();
  const showCents = business.settings?.show_cents ?? true;
  return useMemo(() => (cents: number) => formatMoney(cents, { showCents }), [showCents]);
}

export function priceRange(variants: VariantRow[], money: (c: number) => string): string {
  const active = variants.filter((v) => v.active);
  const list = active.length ? active : variants;
  if (list.length === 0) return '—';
  const prices = list.map((v) => v.price);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return min === max ? money(min) : `${money(min)} – ${money(max)}`;
}
